// components/FeatureRenderer/FeatureRenderer.jsx
import React, { useState, useEffect } from 'react';
import DimensionInput from './DimensionInput';
import HardwareSelector from './HardwareSelector';
import LocationSelector from './LocationSelector';

const FeatureRenderer = ({
    features,
    values,
    onChange,
    productType,
    parentProductType
}) => {
    const [visibleFeatures, setVisibleFeatures] = useState([]);

    useEffect(() => {
        const processed = features
            .map(feature => {
                // 1. No dependencies → always visible
                if (!feature.dependencies || feature.dependencies.length === 0) {
                    return { ...feature };
                }

                // 2. Check if ANY dependency condition matches
                const matchedDependency = feature.dependencies.find(dep => {
                    const dependentValue = values[dep.featureSlug];
                    return dependentValue === dep.condition;
                });

                // 3. If no dependency matched → hide feature
                if (!matchedDependency) {
                    return null;
                }

                // 4. If matched → filter options (if allowedValues exist)
                let updatedFeature = { ...feature };

                if (
                    matchedDependency.allowedValues &&
                    feature.options &&
                    feature.options.length > 0
                ) {
                    updatedFeature.options = feature.options.filter(opt =>
                        matchedDependency.allowedValues.includes(opt.value)
                    );
                }

                return updatedFeature;
            })
            .filter(Boolean); // remove nulls

        setVisibleFeatures(processed);
    }, [features, values]);

    const parseValue = (value, options) => {
        // Find the option that matches this value
        const option = options.find(opt => String(opt.value) === String(value));
        if (option) {
            // Return the value in its original type
            return option.value;
        }
        return value;
    };

    const renderFeature = (feature) => {
        const value = values[feature.slug];

        switch (feature.uiConfig?.component) {
            case 'RadioGroup':
                return (
                    <div className="mb-4" key={feature.slug}>
                        <label className="form-label fw-medium">
                            {feature.name} {feature.required && <span className="text-danger">*</span>}
                        </label>
                        <div className="d-flex flex-wrap gap-3">
                            {feature.options.map(option => (
                                <div className="form-check" key={option.value}>
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name={feature.slug}
                                        id={`${feature.slug}-${option.value}`}
                                        value={option.value}
                                        checked={value === option.value || (!value && option.default)}
                                        onChange={(e) => {
                                            const parsedValue = parseValue(e.target.value, feature.options);
                                            onChange(feature.slug, parsedValue);
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor={`${feature.slug}-${option.value}`}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {feature.description && (
                            <small className="text-muted d-block mt-1">{feature.description}</small>
                        )}
                    </div>
                );

            case 'Select':
                return (
                    <div className="mb-4" key={feature.slug}>
                        <label className="form-label fw-medium">
                            {feature.name} {feature.required && <span className="text-danger">*</span>}
                        </label>
                        <select
                            className="form-select"
                            value={value || feature.default || ''}
                            onChange={(e) => onChange(feature.slug, e.target.value)}
                        >
                            <option value="">Select {feature.name}</option>
                            {feature.options.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {feature.description && (
                            <small className="text-muted d-block mt-1">{feature.description}</small>
                        )}
                    </div>
                );

            case 'DimensionInput':
                return (
                    <DimensionInput
                        key={feature.slug}
                        feature={feature}
                        value={value}
                        onChange={(val) => onChange(feature.slug, val)}
                    />
                );

            case 'HardwareSelector':
                return (
                    <HardwareSelector
                        key={feature.slug}
                        feature={feature}
                        value={value}
                        onChange={(val) => onChange(feature.slug, val)}
                        location={values.location}
                        values={values}
                        productType={parentProductType}
                    />
                );

            case 'LocationSelector':
                return (
                    <LocationSelector
                        key={feature.slug}
                        feature={feature}
                        value={value}
                        onChange={(val) => onChange(feature.slug, val)}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <div>
            {visibleFeatures.map(feature => renderFeature(feature))}
        </div>
    );
};

export default FeatureRenderer;